import { QuantitySelector } from '@trellis/roots/forms'
import { Label } from '@trellis/roots/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@trellis/roots/select'
import { Typography } from '@trellis/roots/typography'
import type { RecurrenceFrequency, RecurrenceRule } from '@trellis/shared'
import {
  DAY_ABBREVIATIONS,
  formatRecurrence,
  LEGACY_FREQUENCY_INTERVAL,
} from '@trellis/shared'
import { useEffect, useRef } from 'react'

import { cn } from '@/lib/utils'

type RecurrenceUnit = 'day' | 'week' | 'month' | 'year'

const UNIT_LABELS: Record<RecurrenceUnit, string> = {
  day: 'day',
  week: 'week',
  month: 'month',
  year: 'year',
}

const UNIT_TO_FREQUENCY: Record<RecurrenceUnit, RecurrenceFrequency> = {
  day: 'DAILY',
  week: 'WEEKLY',
  month: 'MONTHLY',
  year: 'ANNUALLY',
}

function unitForFrequency(frequency: RecurrenceFrequency): RecurrenceUnit {
  switch (frequency) {
    case 'DAILY':
      return 'day'
    case 'WEEKLY':
    case 'FORTNIGHTLY':
      return 'week'
    case 'MONTHLY':
    case 'QUARTERLY':
      return 'month'
    case 'ANNUALLY':
      return 'year'
  }
}

/** Legacy frequencies carry an implicit interval never persisted on the record itself. */
function displayInterval(frequency: RecurrenceFrequency, interval: number): number {
  if (frequency === 'FORTNIGHTLY' || frequency === 'QUARTERLY') {
    return LEGACY_FREQUENCY_INTERVAL[frequency]
  }
  return interval
}

function dayOfWeekOf(dateString: string): number {
  return new Date(`${dateString}T00:00:00`).getDay()
}

/**
 * Picker display order (Mon…Sun); values still match {@link DAY_ABBREVIATIONS}'s
 * Date#getDay() indices.
 */
const WEEKDAY_PICKER_ORDER = [1, 2, 3, 4, 5, 6, 0]

interface RecurrenceRuleBuilderProps {
  frequency: RecurrenceFrequency
  interval: number
  daysOfWeek: number[]
  /** The schedule's "Starts" date (yyyy-mm-dd) — derives dayOfMonth/monthOfYear. */
  startDate: string
  dayOfMonth: number | undefined
  monthOfYear: number | undefined
  onChange: (
    update: Partial<
      Pick<
        RecurrenceRule,
        'frequency' | 'interval' | 'daysOfWeek' | 'dayOfMonth' | 'monthOfYear'
      >
    >,
  ) => void
}

export function RecurrenceRuleBuilder({
  frequency,
  interval,
  daysOfWeek,
  startDate,
  dayOfMonth,
  monthOfYear,
  onChange,
}: RecurrenceRuleBuilderProps) {
  const unit = unitForFrequency(frequency)
  const shownInterval = displayInterval(frequency, interval)
  const preview = formatRecurrence({
    frequency,
    interval,
    daysOfWeek,
    dayOfMonth,
    monthOfYear,
  })

  const isFirstRenderRef = useRef(true)
  const previousUnitRef = useRef(unit)
  const previousStartDateRef = useRef(startDate)

  useEffect(() => {
    const unitChanged = previousUnitRef.current !== unit
    const startDateChanged = previousStartDateRef.current !== startDate
    previousUnitRef.current = unit
    previousStartDateRef.current = startDate

    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false
      return
    }
    if ((unit !== 'month' && unit !== 'year') || !startDate) return
    if (!unitChanged && !startDateChanged) return

    const startDateObj = new Date(`${startDate}T00:00:00`)
    onChange({
      dayOfMonth: startDateObj.getDate(),
      ...(unit === 'year' ? { monthOfYear: startDateObj.getMonth() + 1 } : {}),
    })
  }, [unit, startDate])

  function handleUnitChange(value: string) {
    const nextUnit = value as RecurrenceUnit
    const update: Partial<RecurrenceRule> = {
      frequency: UNIT_TO_FREQUENCY[nextUnit],
      interval: shownInterval,
    }

    if (nextUnit === 'week') {
      update.daysOfWeek =
        daysOfWeek.length > 0 ? daysOfWeek : startDate ? [dayOfWeekOf(startDate)] : []
    } else {
      update.daysOfWeek = []
    }

    onChange(update)
  }

  function handleIntervalChange(nextInterval: number) {
    onChange({
      frequency: UNIT_TO_FREQUENCY[unit],
      interval: nextInterval,
    })
  }

  function handleWeekdayToggle(dayIndex: number) {
    const nextDaysOfWeek = daysOfWeek.includes(dayIndex)
      ? daysOfWeek.filter((selectedDay) => selectedDay !== dayIndex)
      : [...daysOfWeek, dayIndex]
    onChange({
      frequency: UNIT_TO_FREQUENCY[unit],
      interval: shownInterval,
      daysOfWeek: nextDaysOfWeek,
    })
  }

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <Label>Repeat every</Label>
        <div className="flex items-center gap-2">
          <QuantitySelector
            min={1}
            value={shownInterval}
            onChange={handleIntervalChange}
            ariaLabel="Repeat interval"
          />
          <Select value={unit} onValueChange={handleUnitChange}>
            <SelectTrigger aria-label="Repeat unit" className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(UNIT_LABELS) as RecurrenceUnit[]).map((unitOption) => (
                <SelectItem key={unitOption} value={unitOption}>
                  {UNIT_LABELS[unitOption]}
                  {shownInterval !== 1 ? 's' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {unit === 'week' && (
        <div className="space-y-2">
          <Label>Repeat on</Label>
          <div className="flex items-center gap-1.5">
            {WEEKDAY_PICKER_ORDER.map((dayIndex) => {
              const label = DAY_ABBREVIATIONS[dayIndex]
              const isSelected = daysOfWeek.includes(dayIndex)
              return (
                <button
                  key={dayIndex}
                  type="button"
                  aria-pressed={isSelected}
                  aria-label={label}
                  onClick={() => handleWeekdayToggle(dayIndex)}
                  className={cn(
                    'flex h-9 w-9 items-center justify-center rounded-full text-xs sm:h-8 sm:w-8 font-medium transition-colors',
                    isSelected
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/70',
                  )}
                >
                  {label[0]}
                </button>
              )
            })}
          </div>
        </div>
      )}

      <Typography variant="body" color="muted">
        {preview}
      </Typography>
    </div>
  )
}
